import moment from 'moment-timezone';
import React from 'react';
import { Dropdown, Label, Loader, Menu, Message, Segment, SemanticCOLORS, Table } from 'semantic-ui-react';
import styled from 'styled-components';

import IconButton from '../../../components/IconButton';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import { DateOnlyToStringDate } from '../../../services/date';
import { numberToMoneyString } from '../../../services/masks';
import { getListEmptyMessage } from '../../../utils/estateUnitEmptyState.util';

import { statusLabelMap } from '../constants';
import { BondInsuranceInvoiceAction, BondInsuranceInvoiceRow } from '../types';

type Props = {
  isLoading: boolean;
  items: BondInsuranceInvoiceRow[];
  currentPage: number;
  total: number;
  setCurrentPage: (page: number) => void;
  showEstateColumn: boolean;
  userRole?: string;
  userEstateUnitId?: string | null;
  actionLoading: { invoiceId: string; action: BondInsuranceInvoiceAction } | null;
  onOpenDetails: (invoice: BondInsuranceInvoiceRow) => void;
  onDownloadBillet: (invoice: BondInsuranceInvoiceRow) => void;
};

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const statusColorMap: Record<string, SemanticCOLORS> = {
  OPEN: 'yellow',
  CLOSED: 'green',
  CANCELED: 'red'
};

const stopRowOpen = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

const BondInsuranceInvoicesTable: React.FC<Props> = ({
  isLoading,
  items,
  currentPage,
  total,
  setCurrentPage,
  showEstateColumn,
  userRole,
  userEstateUnitId,
  actionLoading,
  onOpenDetails,
  onDownloadBillet
}) => {
  const columnCount = showEstateColumn ? 11 : 10;

  if (isLoading) {
    return (
      <>
        <Segment basic style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader active inline="centered">
            Carregando faturas de fiança...
          </Loader>
        </Segment>
        <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
      </>
    );
  }

  return (
    <>
      <TableWrapper>
        <Table celled striped unstackable selectable style={{ minWidth: showEstateColumn ? 1180 : 1040 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Gerado em</Table.HeaderCell>
              <Table.HeaderCell>Vencimento</Table.HeaderCell>
              <Table.HeaderCell>Pagamento</Table.HeaderCell>
              {showEstateColumn && <Table.HeaderCell>Imobiliária</Table.HeaderCell>}
              <Table.HeaderCell>Unidade</Table.HeaderCell>
              <Table.HeaderCell>Seguradora</Table.HeaderCell>
              <Table.HeaderCell>Nº fatura</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Valor</Table.HeaderCell>
              <Table.HeaderCell>Gestor</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columnCount}>
                  <Message info style={{ margin: 0 }}>
                    <Message.Header>Nenhuma fatura encontrada</Message.Header>
                    <p style={{ margin: '0.5em 0 0' }}>
                      {getListEmptyMessage(userRole, userEstateUnitId)}
                    </p>
                  </Message>
                </Table.Cell>
              </Table.Row>
            ) : (
              items.map(invoice => {
                const createdAt = invoice.createdAt
                  ? moment(invoice.createdAt).format('DD/MM/YYYY HH:mm')
                  : '-';
                const statusColor = statusColorMap[invoice.status] || 'grey';
                const billetLoading =
                  actionLoading?.invoiceId === invoice.id &&
                  actionLoading.action === 'download-billet';

                return (
                  <Table.Row
                    key={invoice.id}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => onOpenDetails(invoice)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenDetails(invoice);
                      }
                    }}
                    aria-label={`Abrir detalhes da fatura ${invoice.invoiceNumber || invoice.billetNumber || invoice.id}`}
                  >
                    <Table.Cell>{createdAt}</Table.Cell>
                    <Table.Cell>{invoice.dueDate ? DateOnlyToStringDate(invoice.dueDate) : '-'}</Table.Cell>
                    <Table.Cell>{invoice.paymentDate ? DateOnlyToStringDate(invoice.paymentDate) : '-'}</Table.Cell>
                    {showEstateColumn && <Table.Cell>{invoice.estate?.name || '-'}</Table.Cell>}
                    <Table.Cell>{invoice.estateUnit?.name || '-'}</Table.Cell>
                    <Table.Cell>{invoice.insurer?.name || '-'}</Table.Cell>
                    <Table.Cell>{invoice.invoiceNumber || invoice.billetNumber || '-'}</Table.Cell>
                    <Table.Cell textAlign="right">{numberToMoneyString(Number(invoice.amount || 0))}</Table.Cell>
                    <Table.Cell>{invoice.estate?.user?.name || '-'}</Table.Cell>
                    <Table.Cell>
                      <Label color={statusColor} size="small">
                        {statusLabelMap[invoice.status] || invoice.status}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center" onClick={stopRowOpen}>
                      <Menu compact>
                        <Menu.Item>
                          <Dropdown
                            trigger={<IconButton name={billetLoading ? 'spinner' : 'ellipsis vertical'} loading={billetLoading} />}
                            icon={null}
                            direction="left"
                          >
                            <Dropdown.Menu style={{ zIndex: 1000 }}>
                              <Dropdown.Item
                                icon="eye"
                                text="Ver detalhes"
                                disabled={Boolean(actionLoading)}
                                onClick={() => onOpenDetails(invoice)}
                              />
                              <Dropdown.Item
                                icon="download"
                                text="Baixar boleto"
                                disabled={Boolean(actionLoading)}
                                onClick={() => onDownloadBillet(invoice)}
                              />
                            </Dropdown.Menu>
                          </Dropdown>
                        </Menu.Item>
                      </Menu>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </TableWrapper>
      <TablePaginationMenu currentPage={currentPage} dataCount={total} setCurrentPage={setCurrentPage} />
    </>
  );
};

export default BondInsuranceInvoicesTable;
